import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Magnetic, scrollTo, ease } from "../lib/motion";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      {/* luxury light background */}
      <div className="absolute inset-0 -z-10 overflow-hidden bg-[#070708]">
        <div className="lux-drift absolute left-1/2 top-[22%] h-[620px] w-[980px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(206,212,220,0.11),transparent_66%)] blur-2xl" />
        <div className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/[0.035] blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-5%,rgba(255,255,255,0.06),transparent_45%)]" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#070708] to-transparent" />
      </div>

      <motion.div style={{ y, opacity }} className="mx-auto w-full max-w-7xl px-6 pt-32 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mb-9 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#9aa2ac] backdrop-blur"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#e6eaef]" />
          The future of business, deployed today
        </motion.div>

        <h1 className="font-display text-[12.5vw] font-semibold leading-[0.94] tracking-normal md:text-[7.25rem]">
          <MaskedLines
            lines={["Your Next Employee", "Isn't Human."]}
            delay={0.4}
            lineClass="text-gradient"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1, ease }}
          className="mt-9 max-w-xl text-base leading-relaxed text-[#9aa2ac] md:text-lg"
          data-testid="hero-subheading"
        >
          We design AI-powered systems that automate work, increase productivity and help
          businesses grow — without increasing headcount.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1, ease }}
          className="mt-12 flex items-center gap-6"
        >
          <Magnetic>
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-[#070708] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              Book a Strategy Session
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Magnetic>
          <button
            onClick={() => scrollTo("#employees")}
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2 text-sm font-medium text-[#9aa2ac] transition-colors duration-300 hover:text-white"
          >
            Explore AI Employees
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-[#6a7079]"
      >
        Scroll
      </motion.div>
    </section>
  );
}
